
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { DefineCommand, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { BoardInfo } from '../../canvas/BoardInfo';
import { resolveSubject } from '../../canvas/commands/CommandUtil';
import { select } from '../../canvas/commands/SelectionCommands';
import { EditorSession } from '../../canvas/EditorSession';
import { Physicals } from '../../canvas/entities/Physicals';
import { JiraSprint } from './JiraSprint';
import { JiraSprintImportFlow } from './JiraSprintImportFlow';
import { JiraSprintRecordManager } from './JiraSprintRecordManager';

export const jiraSprintsLoad = DefineCommand({
    name: 'jiraSprintsLoad',
    async impl(cc) {

        const api = cc.resolve(ApiClient);
        const objects = cc.resolve(ObjectManager);
        const allSprints = Array.from(objects.getAll(JiraSprint));
        const board = cc.resolve(EditorSession).get(BoardInfo);

        const loadableSprints = allSprints.filter(x => !!x.jiraSprintId);
        if (!!loadableSprints.length) {
            await api.exec(v2.jira.sprints.load({
                boardId: board.id,
                jiraSprintIds: loadableSprints.map(x => x.jiraSprintId),
            }));
        }
    },
});

export const jiraSprintImport = DefineCommand({
    name: 'jiraSprintImport',
    label: 'Jira Sprint: Import...',
    ui: {
        flow: JiraSprintImportFlow,
    },
    impl(cc, { jiraSprintId }: { jiraSprintId: string | number }) {

        if (!jiraSprintId) return;

        const objects = cc.resolve(ObjectManager);
        const updateKey = `jiraCreateSprint:${uuid()}`;

        const sprint = objects.update(u => {
            u.set('key', updateKey);
            return Physicals.createAtCameraPosition(cc, JiraSprint, nc => {
                nc.jiraSprintId = `${jiraSprintId}`;
            });
        });

        cc.dispatch(select, [sprint.id]);
    },
});

export const jiraSprintSetName = DefineCommand({
    name: 'jiraSprintSetName',
    label: 'Jira Sprint: Set Name',
    impl(cc, { targetId, name }: { targetId?: string, name: string }) {

        const recordManager = cc.resolve(JiraSprintRecordManager);
        const subject = resolveSubject(cc, targetId)?.as(JiraSprint);
        if (!subject) return;

        recordManager.patch({
            jiraSprintId: subject.jiraSprintId,
            name,
        });
    },
});

export const jiraSprintSetStartDate = DefineCommand({
    name: 'jiraSprintSetStartDate',
    label: 'Jira Sprint: Set Start Date',
    impl(cc, { targetId, startDate }: { targetId?: string, startDate: string }) {

        const recordManager = cc.resolve(JiraSprintRecordManager);
        const subject = resolveSubject(cc, targetId)?.as(JiraSprint);
        if (!subject) return;

        recordManager.patch({
            jiraSprintId: subject.jiraSprintId,
            startDate,
        });
    },
});

export const jiraSprintSetEndDate = DefineCommand({
    name: 'jiraSprintSetEndDate',
    label: 'Jira Sprint: Set End Date',
    impl(cc, { targetId, endDate }: { targetId?: string, endDate: string }) {

        const recordManager = cc.resolve(JiraSprintRecordManager);
        const subject = resolveSubject(cc, targetId)?.as(JiraSprint);
        if (!subject) return;

        recordManager.patch({
            jiraSprintId: subject.jiraSprintId,
            endDate,
        });
    },
});

export const jiraSprintSetGoal = DefineCommand({
    name: 'jiraSprintSetGoal',
    label: 'Jira Sprint: Set Goal',
    impl(cc, { targetId, goal }: { targetId?: string, goal: string }) {

        const recordManager = cc.resolve(JiraSprintRecordManager);
        const subject = resolveSubject(cc, targetId)?.as(JiraSprint);
        if (!subject) return;

        recordManager.patch({
            jiraSprintId: subject.jiraSprintId,
            goal,
        });
    },
});

export const inJiraSprintCommands = () => inCommands(
    jiraSprintsLoad,
    jiraSprintImport,
    jiraSprintSetName,
    jiraSprintSetStartDate,
    jiraSprintSetEndDate,
    jiraSprintSetGoal,
);

